import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Divider,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  tertiaryButtonBackground,
  blueButton,
  cardBorder,
  black,
  blue,
  blackText
} from '../../constants/Colors';
import {
  genreList,
  purpose as purposeList,
  budget as budgetList,
  mediaTypes as mediaTypeList,
  menuList
} from '../../constants/Select';

import CustomDatePicker from './CustomDatePicker';
import CustomSelectField from './CustomSelectField';

import type { Dayjs } from 'dayjs';

const styles = {
  blueButton,
  button: {
    boxShadow: 0,
    height: '36px',
    minWidth: '148px',
    width: '192px'
  },
  buttonAndChips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  buttons: {
    '@media screen and (max-width:960px)': {
      justifyContent: 'center',
      padding: '24px 16px'
    },
    columnGap: '16px',
    rowGap: '16px'
  },
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px'
    },
    boxShadow: 0,
    margin: '24px'
  },
  cardBorder,
  cardContents: {
    '@media screen and (max-width:960px)': {
      border: '1px solid #222',
      margin: 0
    },
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    margin: '24px'
  },
  chips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  clearButton: {
    color: black
  },
  date: {
    ml: '24px',
    mt: '4px',
    width: '65%'
  },
  demoTableBorder: {
    '@media screen and (max-width:1536px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column',
      p: '12px 16px 0'
    },
    border: '1px solid #DBDBE0'
  },
  demoTableBorderLeft: {
    '@media screen and (max-width:1536px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column'
    },
    borderBottom: '1px solid #DBDBE0',
    borderLeft: '1px solid #DBDBE0'
  },
  demoTableBorderRight: {
    '@media screen and (max-width:1536px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column'
    },
    borderBottom: '1px solid #DBDBE0',
    borderRight: '1px solid #DBDBE0'
  },
  demoTableContent: {
    '@media screen and (max-width:700px)': {
      width: '100%'
    },
    height: '60px',
    width: '70%'
  },
  demoTableOption: {
    width: '120px'
  },
  demoTablePadding: {
    p: '12px 24px'
  },
  demoTablePercentBetween: {
    '@media screen and (max-width:700px)': {
      p: '6px 4px'
    },
    p: '6px 14px'
  },
  demoTablePercents: {
    '@media screen and (max-width:700px)': {
      p: '12px 16px'
    },
    p: '12px 24px'
  },
  demoTableText: {
    '@media screen and (max-width:1536px)': {
      pl: 0,
      whiteSpace: 'normal'
    },
    fontSize: '14px',
    fontWeight: '700',
    pl: '24px',
    whiteSpace: 'pre-wrap'
  },
  demoTableTextbg: {
    '@media screen and (max-width:700px)': {
      height: '44px',
      width: '100%'
    },
    bgcolor: '#F2F2F2',
    width: '140px'
  },
  demoTableTopBorderLeft: {
    '@media screen and (max-width:1536px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column'
    },
    borderBottom: '1px solid #DBDBE0',
    borderLeft: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0'
  },
  demoTableTopBorderRight: {
    '@media screen and (max-width:1536px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column'
    },
    borderBottom: '1px solid #DBDBE0',
    borderRight: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0'
  },
  demoTableWidth: {
    '@media screen and (max-width:700px)': {
      width: '311px'
    },
    width: '596px'
  },
  divider: {
    color: '#DBDBE0',
    pt: '24px'
  },
  isActive: {
    display: 'flex'
  },
  isNotActive: {
    display: 'none'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalButton: {
    width: '100%'
  },
  modalOption: {
    '&.MuiFormControlLabel-root .MuiFormControlLabel-label': {
      '@media screen and (max-width:700px)': {
        fontSize: '12px'
      },
      fontSize: '14px',
      pr: '3px'
    },
    '@media screen and (max-width:700px)': {
      padding: 0
    },
    bgcolor: '#F2F2F2',
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: 0,
    padding: '0 6px',
    width: '290px'
  },
  modalOptionAllWidth: {
    width: '100%'
  },
  modalOptionWidth: {
    '@media screen and (max-width:700px)': {
      width: '151px'
    }
  },
  modalOptions: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    mb: '24px',
    rowGap: '8px'
  },
  modalText: {
    fontSize: '22px',
    padding: 0
  },
  modalTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0 0 24px 0'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  searchBox: {
    '@media screen and (max-width:960px)': {
      rowGap: 0
    },
    flexDirection: 'column',
    rowGap: '24px'
  },
  searchSpButton: {
    '@media screen and (max-width:960px)': {
      display: 'flex'
    },
    bgcolor: '#F2F2F2',
    color: black,
    display: 'none',
    flexDirection: 'row',
    height: '48px',
    justifyContent: 'space-between',
    padding: '12px 16px',
    textAlign: 'left'
  },
  select: {
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: black
    },
    '@media screen and (max-width:480px)': {
      width: '100%'
    },
    height: '36px',
    padding: 0,
    width: '336px'
  },
  selectPercent: {
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: black
    },
    '@media screen and (max-width:700px)': {
      width: '126px'
    },
    height: '36px',
    padding: 0,
    width: '140px'
  },
  selectedChip: {
    '&.MuiChip-root .MuiChip-deleteIcon': {
      color: black
    }
  },
  table: {
    boxShadow: 0,
    maxWidth: '100%'
  },
  tableButton: {
    color: blue,
    mr: '16px',
    padding: '0px'
  },
  tableItem: {
    '@media screen and (max-width:960px)': {
      bgcolor: '#FFF',
      padding: 0
    },
    bgcolor: '#F2F2F2',
    padding: '18px 24px',
    width: '195px'
  },
  tableRow: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      padding: '12px 16px',
      rowGap: '8px'
    },
    border: '1px solid #E5E5E5',
    flexDirection: 'row',
    padding: 0,
    rowGap: 0
  },
  tableValue: {
    '@media screen and (max-width:960px)': {
      padding: 0
    },
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    padding: '12px 24px'
  },
  tertiaryButtonBackground,
  text: {
    fontSize: '14px',
    mb: '8px'
  },
  textfield: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'inherit'
      },
      '&:hover fieldset': {
        borderColor: 'inherit'
      }
    },
    '@media screen and (max-width:1536px)': {
      pl: 0
    },
    '@media screen and (max-width:480px)': {
      mt: '4px',
      pb: '24px'
    },
    mt: '10px',
    pl: '24px'
  }
};

interface Props {
  handleChange: (
    issueTitle: string,
    startDate: { start: Dayjs | null; end: Dayjs | null },
    applicationDate: { start: Dayjs | null; end: Dayjs | null },
    genre: string | number | null,
    budget: string | number | null,
    purpose: string | number | null,
    mediaType: string | number | null,
    menu: number | null
  ) => void;
}

const IssueSearchCard = (props: Props): React.ReactElement => {
  const { handleChange } = props;
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExChange = (): void => {
    setIsExpanded(!isExpanded);
  };

  const [issueTitle, setIssueTitle] = useState<string>('');
  const [startDate, setStartDate] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({
    end: null,
    start: null
  });
  const [applicationDate, setApplicationDate] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({
    end: null,
    start: null
  });

  const [genre, setGenre] = useState<string | number | null>(0);
  const [budget, setBudget] = useState<string | number | null>(0);
  const [purpose, setPurpose] = useState<string | number | null>(0);
  const [mediaType, setMediaType] = useState<string | number | null>(0);
  const [menu, setMenu] = useState<number | null>(0);

  const genres = genreList();
  const budgets = budgetList();
  const purposes = purposeList();
  const mediaTypes = mediaTypeList();
  const menus = menuList();

  const resetSearchParams = (): void => {
    setIssueTitle('');
    setStartDate({
      end: null,
      start: null
    });
    setApplicationDate({
      end: null,
      start: null
    });
    setGenre(0);
    setBudget(0);
    setPurpose(0);
    setMediaType(0);
    setMenu(0);
  };

  return (
    <>
      {/* SP */}
      <Typography
        component="div"
        sx={{
          '@media screen and (max-width: 480px)': {
            margin: '16px'
          },
          backgroundColor: '#F3F3F3',
          margin: '16px 24px'
        }}
      >
        <Accordion
          sx={[
            {
              '@media screen and (min-width:1536px)': {
                display: 'none'
              },
              border: '1px solid #222',
              boxShadow: 'none'
            }
          ]}
          onChange={() => handleExChange()}
          expanded={isExpanded}
        >
          <AccordionSummary
            expandIcon={isExpanded ? <RemoveIcon /> : <AddIcon />}
            sx={{
              backgroundColor: isExpanded ? '#F3F3F3' : 'white'
            }}
          >
            <Typography>{t('media.searchBlock')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Typography component="p" sx={styles.demoTableText}>
                    {t('orientation.issueName')}
                  </Typography>
                  <TextField
                    fullWidth
                    value={issueTitle}
                    onChange={(e) => {
                      setIssueTitle(e.target.value);
                    }}
                    size="small"
                    sx={styles.textfield}
                    placeholder={t('media.searchFreeWordPlaceholder')}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.productGenre')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={genre}
                      onChange={(e) =>
                        setGenre(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={genres}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.purpose')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={purpose}
                      onChange={(e) =>
                        setPurpose(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={purposes}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.startDate')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={startDate.start}
                      setValue={(e) => {
                        setStartDate({ ...startDate, start: e });
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      sx={[
                        styles.demoTableText,
                        { pt: '12px', textAlign: 'center' }
                      ]}
                      component="p"
                    >
                      〜
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={startDate.end}
                      setValue={(e) => {
                        setStartDate({ ...startDate, end: e });
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.applicationDate')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={applicationDate.start}
                      setValue={(e) => {
                        setApplicationDate({ ...applicationDate, start: e });
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      sx={[
                        styles.demoTableText,
                        { pt: '12px', textAlign: 'center' }
                      ]}
                      component="p"
                    >
                      〜
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={applicationDate.end}
                      setValue={(e) => {
                        setApplicationDate({ ...applicationDate, end: e });
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.budget')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={budget}
                      onChange={(e) =>
                        setBudget(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={budgets}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.mediaTypeSimple')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={mediaType}
                      onChange={(e) =>
                        setMediaType(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={mediaTypes}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.advertismentMenu')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={menu}
                      onChange={(e) => setMenu(e.target.value as number | null)}
                      isRequired={false}
                      width={'search'}
                      list={menus}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container sx={{ pb: '12px' }}>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item container>
                  <Grid item xs={6} justifyContent={'center'}>
                    <Button
                      onClick={() => {
                        resetSearchParams();
                        handleChange(
                          '',
                          { end: null, start: null },
                          { end: null, start: null },
                          0,
                          0,
                          0,
                          0,
                          0
                        );
                      }}
                      sx={tertiaryButtonBackground}
                      fullWidth
                    >
                      <Typography sx={[blackText, { fontSize: '14px' }]}>
                        {t('issue.clear')}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={6} justifyContent={'center'}>
                    <Button
                      sx={[
                        blueButton,
                        { fontSize: '14px', ml: '10%', width: '90%' }
                      ]}
                      onClick={() =>
                        handleChange(
                          issueTitle,
                          startDate,
                          applicationDate,
                          genre,
                          budget,
                          purpose,
                          mediaType,
                          menu
                        )
                      }
                    >
                      {t('issue.search')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Typography>

      {/* PC */}
      <Card
        sx={[
          styles.card,
          {
            '@media screen and (max-width:1536px)': {
              display: 'none'
            }
          }
        ]}
      >
        <Card sx={styles.card}>
          <Grid container>
            <Grid item xs={6} sx={styles.demoTableTopBorderLeft} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('orientation.product')}
                </Typography>
              </Grid>
              <Grid item sx={styles.demoTableContent} container>
                <TextField
                  fullWidth
                  value={issueTitle}
                  onChange={(e) => setIssueTitle(e.target.value)}
                  size="small"
                  sx={styles.textfield}
                  placeholder={t('media.searchFreeWordPlaceholder')}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableTopBorderRight} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.applicationDate')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.date]}>
                <Typography component="div" style={{ display: 'flex' }}>
                  <Typography component="div">
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={applicationDate.start}
                      setValue={(e) => {
                        setApplicationDate({ ...applicationDate, start: e });
                      }}
                    />
                  </Typography>
                  <Typography component="div">
                    <Typography sx={{ p: '12px 12px 0' }}>〜</Typography>
                  </Typography>
                  <Typography component="div">
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={applicationDate.end}
                      setValue={(e) => {
                        setApplicationDate({ ...applicationDate, end: e });
                      }}
                    />
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderLeft} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.genre')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.textfield]}>
                <CustomSelectField
                  blank
                  value={genre}
                  onChange={(e) => setGenre(e.target.value as string | null)}
                  isRequired={false}
                  width={'search'}
                  list={genres}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderRight} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.budget')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.textfield]}>
                <CustomSelectField
                  blank
                  value={budget}
                  onChange={(e) => setBudget(e.target.value as string | null)}
                  isRequired={false}
                  width={'search'}
                  list={budgets}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderLeft} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.purpose')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.textfield]}>
                <CustomSelectField
                  blank
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value as string | null)}
                  isRequired={false}
                  width={'search'}
                  list={purposes}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderRight} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.mediaTypeSimple')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.textfield]}>
                <CustomSelectField
                  blank
                  value={mediaType}
                  onChange={(e) =>
                    setMediaType(e.target.value as string | null)
                  }
                  isRequired={false}
                  width={'search'}
                  list={mediaTypes}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderLeft} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.startDate')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.date]}>
                <Typography component="div" style={{ display: 'flex' }}>
                  <Typography component="div">
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={startDate.start}
                      setValue={(e) => {
                        setStartDate({ ...startDate, start: e });
                      }}
                    />
                  </Typography>
                  <Typography component="div">
                    <Typography sx={{ p: '12px 12px 0' }}>〜</Typography>
                  </Typography>
                  <Typography component="div">
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={startDate.end}
                      setValue={(e) => {
                        setStartDate({ ...startDate, end: e });
                      }}
                    />
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={6} sx={styles.demoTableBorderRight} container>
              <Grid
                item
                sx={styles.demoTableTextbg}
                container
                alignItems="center"
              >
                <Typography component="p" sx={styles.demoTableText}>
                  {t('issue.advertismentMenu')}
                </Typography>
              </Grid>
              <Grid item sx={[styles.demoTableContent, styles.textfield]}>
                <CustomSelectField
                  blank
                  value={menu}
                  onChange={(e) => setMenu(e.target.value as number | null)}
                  isRequired={false}
                  width={'search'}
                  list={menus}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            direction={'row'}
            justifyContent={'flex-end'}
            columnGap={'16px'}
            marginTop={'24px'}
          >
            <Grid item>
              <Button
                onClick={() => {
                  resetSearchParams();
                  handleChange(
                    '',
                    { end: null, start: null },
                    { end: null, start: null },
                    0,
                    0,
                    0,
                    0,
                    0
                  );
                }}
                sx={tertiaryButtonBackground}
              >
                <Typography
                  sx={[blackText, { fontSize: '14px', width: '200px' }]}
                >
                  {t('issue.clear')}
                </Typography>
              </Button>
            </Grid>
            <Grid item>
              <Button
                sx={[blueButton, { fontSize: '14px', width: '200px' }]}
                onClick={() =>
                  handleChange(
                    issueTitle,
                    startDate,
                    applicationDate,
                    genre,
                    budget,
                    purpose,
                    mediaType,
                    menu
                  )
                }
              >
                {t('issue.search')}
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Card>
    </>
  );
};

export default IssueSearchCard;
